import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Trash2, Users } from "lucide-react";

import { AppSidebar } from "@/components/sidebar/app-sidebar";
import {
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
} from "@/components/ui/sidebar";
import { Separator } from "@/components/ui/separator";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import {
  createFeatureFlag,
  deleteFeatureFlag,
  deleteSubscriptionOverride,
  FeatureFlag,
  FeatureFlagWithOverrides,
  getFeatureFlag,
  listFeatureFlags,
  setSubscriptionOverride,
  updateFeatureFlag,
} from "@/services/featureFlagsApi";
import { confirmDialog } from "@/components/ui/confirm-dialog";

export default function FeatureFlagsPage() {
  const navigate = useNavigate();
  const [flags, setFlags] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState<FeatureFlagWithOverrides | null>(null);

  // Create form state
  const [newKey, setNewKey] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [newEnabled, setNewEnabled] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listFeatureFlags(navigate);
      setFlags(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openDetails = async (id: number) => {
    try {
      const data = await getFeatureFlag(id, navigate);
      setSelected(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur inconnue");
    }
  };

  const handleCreate = async () => {
    if (!newKey.trim()) return;
    try {
      await createFeatureFlag(
        {
          key: newKey.trim(),
          description: newDescription.trim() || undefined,
          enabled_by_default: newEnabled,
        },
        navigate,
      );
      setCreateOpen(false);
      setNewKey("");
      setNewDescription("");
      setNewEnabled(false);
      refresh();
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur de creation");
    }
  };

  const handleToggleDefault = async (flag: FeatureFlag, enabled: boolean) => {
    await updateFeatureFlag(flag.id, { enabled_by_default: enabled }, navigate);
    refresh();
  };

  const handleDelete = async (flag: FeatureFlag) => {
    const ok = await confirmDialog({
      title: "Supprimer le flag",
      description: `Le flag "${flag.key}" sera définitivement supprimé.`,
      confirmLabel: "Supprimer",
      destructive: true,
    });
    if (!ok) return;
    await deleteFeatureFlag(flag.id, navigate);
    refresh();
  };

  return (
    <SidebarProvider>
      <AppSidebar />
      <SidebarInset>
        <header className="flex h-16 shrink-0 items-center gap-2 transition-[width,height] ease-linear group-has-[[data-collapsible=icon]]/sidebar-wrapper:h-12">
          <div className="flex items-center gap-2 px-4">
            <SidebarTrigger className="-ml-1" />
            <Separator orientation="vertical" className="mr-2 h-4" />
            <h1 className="text-sm font-medium">Feature flags</h1>
          </div>
        </header>

        <div className="p-6 space-y-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Active ou desactive des fonctionnalites globalement, avec des overrides par abonnement pour l'A/B testing.
            </p>
            <Dialog open={createOpen} onOpenChange={setCreateOpen}>
              <DialogTrigger asChild>
                <Button>
                  <Plus className="mr-2 h-4 w-4" />
                  Nouveau flag
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Creer un feature flag</DialogTitle>
                  <DialogDescription>
                    La cle doit etre unique et stable (utilisee dans le code). Format recommande : snake_case.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-2">
                  <div className="space-y-2">
                    <Label htmlFor="ff-key">Cle</Label>
                    <Input
                      id="ff-key"
                      value={newKey}
                      onChange={(e) => setNewKey(e.target.value)}
                      placeholder="ex: new_dashboard_v2"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="ff-desc">Description</Label>
                    <Input
                      id="ff-desc"
                      value={newDescription}
                      onChange={(e) => setNewDescription(e.target.value)}
                      placeholder="A quoi sert ce flag ?"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="ff-default">Active par defaut</Label>
                    <Switch id="ff-default" checked={newEnabled} onCheckedChange={setNewEnabled} />
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="ghost" onClick={() => setCreateOpen(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleCreate} disabled={!newKey.trim()}>
                    Creer
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {error && <div className="text-sm text-destructive">{error}</div>}

          {loading ? (
            <div className="text-sm text-muted-foreground">Chargement...</div>
          ) : flags.length === 0 ? (
            <div className="text-sm text-muted-foreground">Aucun flag defini. Creez-en un ci-dessus.</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cle</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Par defaut</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flags.map((flag) => (
                    <TableRow key={flag.id}>
                      <TableCell className="font-mono text-xs">{flag.key}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {flag.description || "-"}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={flag.enabled_by_default}
                          onCheckedChange={(v) => handleToggleDefault(flag, v)}
                        />
                      </TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button variant="outline" size="sm" onClick={() => openDetails(flag.id)}>
                          <Users className="mr-2 h-3 w-3" />
                          Overrides
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(flag)}
                          aria-label="Supprimer"
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <OverridesDialog
          flag={selected}
          onClose={() => setSelected(null)}
          onChanged={async () => {
            if (selected) {
              const refreshed = await getFeatureFlag(selected.id, navigate);
              setSelected(refreshed);
            }
          }}
        />
      </SidebarInset>
    </SidebarProvider>
  );
}

interface OverridesDialogProps {
  flag: FeatureFlagWithOverrides | null;
  onClose: () => void;
  onChanged: () => Promise<void>;
}

function OverridesDialog({ flag, onClose, onChanged }: OverridesDialogProps) {
  const navigate = useNavigate();
  const [subId, setSubId] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);

  if (!flag) return null;

  const addOverride = async () => {
    const parsed = parseInt(subId, 10);
    if (!parsed || Number.isNaN(parsed)) return;
    setBusy(true);
    try {
      await setSubscriptionOverride(flag.id, parsed, enabled, navigate);
      setSubId("");
      await onChanged();
    } finally {
      setBusy(false);
    }
  };

  const toggleOverride = async (subscriptionId: number, next: boolean) => {
    await setSubscriptionOverride(flag.id, subscriptionId, next, navigate);
    await onChanged();
  };

  const removeOverride = async (subscriptionId: number) => {
    await deleteSubscriptionOverride(flag.id, subscriptionId, navigate);
    await onChanged();
  };

  return (
    <Dialog open={Boolean(flag)} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-mono text-sm">{flag.key}</DialogTitle>
          <DialogDescription>
            Par defaut :{" "}
            <Badge variant={flag.enabled_by_default ? "default" : "secondary"}>
              {flag.enabled_by_default ? "active" : "inactif"}
            </Badge>
            {flag.description && <span className="block mt-2">{flag.description}</span>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-end gap-2">
            <div className="flex-1 space-y-2">
              <Label htmlFor="ff-sub-id">ID abonnement</Label>
              <Input
                id="ff-sub-id"
                value={subId}
                onChange={(e) => setSubId(e.target.value)}
                placeholder="ex: 42"
                inputMode="numeric"
              />
            </div>
            <div className="flex items-center gap-2 pb-2">
              <Label htmlFor="ff-override-enabled">Active</Label>
              <Switch
                id="ff-override-enabled"
                checked={enabled}
                onCheckedChange={setEnabled}
              />
            </div>
            <Button onClick={addOverride} disabled={busy || !subId.trim()}>
              Ajouter
            </Button>
          </div>

          {flag.overrides.length === 0 ? (
            <div className="text-sm text-muted-foreground">Aucun override defini pour ce flag.</div>
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Abonnement</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Activation</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {flag.overrides.map((o) => (
                    <TableRow key={o.subscription_id}>
                      <TableCell className="font-mono text-xs">#{o.subscription_id}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {o.email || o.user_id || "-"}
                      </TableCell>
                      <TableCell>
                        <Switch
                          checked={o.enabled}
                          onCheckedChange={(v) => toggleOverride(o.subscription_id, v)}
                        />
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeOverride(o.subscription_id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
